/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {RichText} from '@payloadcms/richtext-lexical/react';
import {getPayload} from 'payload';

import {SeoHead} from 'UI/components/layout/SeoHead';
import {Headline} from 'UI/components/layout/Text';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {AppLayout} from 'UI/layouts/AppLayout';

import {getLegalPages} from 'Payload/utils/getLegalPages';
import {buildBreadcrumbJsonLd} from 'UI/utils/jsonLd';

import type {GetStaticPaths, GetStaticProps, InferGetStaticPropsType} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Config, LegalPage} from 'types/payload-types';

const REVALIDATE_SECONDS = 18000;

interface LegalPageProps {
    legalPages: LegalPage[];
    page: LegalPage;
}

/**
 * Rechtliche Seite (Impressum, Datenschutz, etc.). Rendert den Titel
 * und den Rich-Text-Inhalt aus der LegalPages-Collection.
 *
 * @param props      Die Seiten-Props.
 * @param props.page Das LegalPage-Dokument mit Titel und Inhalt.
 * @returns ReactNode der rechtlichen Seite.
 */
const LegalPageView: NextSSGPageWithLayout<typeof getStaticProps> = (
    {page}: InferGetStaticPropsType<typeof getStaticProps>
) => (
    <Page data-cy="LegalPage">
        <SeoHead
            jsonLd={[
                buildBreadcrumbJsonLd([
                    {
                        name: 'Home',
                        url: `${process.env.NEXT_PUBLIC_BASE_URL}/`
                    },
                    {
                        name: page.title,
                        url: `${process.env.NEXT_PUBLIC_BASE_URL}/legal/${page.slug}`
                    }
                ])
            ]}
            title={page.title}
            url={`${process.env.NEXT_PUBLIC_BASE_URL}/legal/${page.slug}`}
        />
        <Headline $size="headline" as="h1">{page.title}</Headline>
        <Content>
            <RichText data={page.content} />
        </Content>
    </Page>
);

LegalPageView.displayName = 'LegalPageView';

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout.
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
LegalPageView.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="settings" legalPages={pageProps.legalPages} isBackVisible realBackLink>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
LegalPageView.getLayoutKey = () => 'legal';

export default LegalPageView;

// ── Static Generation ───────────────────────────────────────

/**
 * Lädt alle Legal-Page-Slugs für die statische Generierung.
 *
 * @returns Pfade und Fallback-Konfiguration.
 */
export const getStaticPaths: GetStaticPaths = () => ({
    fallback: 'blocking',
    paths: []
});

/**
 * Lädt das LegalPage-Dokument anhand des Slugs.
 *
 * @param context                  Der Next.js Static-Props Kontext.
 * @param context.locale           Die aktuelle Locale.
 * @param context.params           Die Route-Parameter mit Slug.
 * @param context.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit dem LegalPage-Dokument oder notFound.
 */
export const getStaticProps: GetStaticProps<LegalPageProps> = async ({locale, params, revalidateReason}) => {
    const slug = params?.slug as string;
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {notFound: true};
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const [result, legalPages] = await Promise.all([
        payload.find({
            collection: 'legal-pages',
            locale: payloadLocale,
            where: {slug: {equals: slug}}
        }),
        getLegalPages(payload, payloadLocale)
    ]);

    const page = result.docs[0] as LegalPage | undefined;

    if (!page) {
        return {notFound: true};
    }

    return {
        props: {
            legalPages,
            page: JSON.parse(JSON.stringify(page)) as LegalPage
        },
        revalidate: REVALIDATE_SECONDS
    };
};

// ── Styled ──────────────────────────────────────────────────

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
    padding: ${spacing(6)};
`;

const Content = styled.div`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    line-height: 1.6;

    h2 {
        font-size: 2rem;
        margin: ${spacing(6)} 0 ${spacing(2)};
    }

    h3 {
        font-size: 1.7rem;
        margin: ${spacing(4)} 0 ${spacing(2)};
    }

    h4 {
        font-size: 1.5rem;
        margin: ${spacing(3)} 0 ${spacing(1)};
    }

    p {
        margin: 0 0 ${spacing(3)};
    }

    ul,
    ol {
        margin: 0 0 ${spacing(3)};
        padding-left: ${spacing(6)};
    }

    a {
        color: ${({theme}) => theme.colors.primaryDefault};
        text-decoration: underline;
    }
`;